import { supabase } from '@/integrations/supabase/client';

// Function to create a shortened URL
export async function shortenUrl(url: string, expiresIn?: number): Promise<string> {
  try {
    // Get the Supabase URL
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }
    
    // Get the access token for authentication
    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    
    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    // Add authorization header if we have an access token
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    
    // Make the API call to the shorten-url function
    const response = await fetch(`${supabaseUrl}/functions/v1/shorten-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ url, expiresIn }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || `Failed to shorten URL: ${response.status}`);
    }
    
    const data = await response.json();
    return data.short_url;
  } catch (error) {
    console.error('Error shortening URL:', error);
    throw error;
  }
}

// Function to resolve a shortened URL
export async function resolveShortUrl(shortId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('shortened_urls')
      .select('original_url, expires_at')
      .eq('id', shortId)
      .maybeSingle();
      
    if (error) {
      throw error;
    }
    
    if (!data) {
      return null;
    }
    
    // Check if the URL has expired
    if (data.expires_at && new Date(data.expires_at) < new Date()) {
      return null;
    }
    
    return data.original_url;
  } catch (error) {
    console.error('Error resolving short URL:', error);
    return null;
  }
} 